// Disciplinary.js
import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControlLabel,
  Checkbox, Snackbar, Alert, Box,
} from '@mui/material';

// Where drafted letters end up - change this wording if your menu calls it something else.
const VAULT_NAME = 'Document Vault';

// Keep in step with ACTION_TYPES in services/disciplinary_service.py and
// TEMPLATE_RULES in services/disciplinary_letters.py.
//   letter       - a letter template exists for this type
//   expiry       - show the expiry date (defaultMonths pre-fills it)
//   improvement  - show "improvement required" and "support" (1st and final written warning letters)
//   standards    - show "standards expected / moving forward" (no-action and final written warning letters)
//   appeal       - show "appeal to" (printed on the no-action and warning letters)
const ACTION_TYPES = [
  { value: 'No Action',             letter: true,  expiry: false, improvement: false, standards: true,  appeal: true  },
  { value: 'Verbal Warning',        letter: false, expiry: true,  improvement: false, standards: false, appeal: false },
  { value: '1st Written Warning',   letter: true,  expiry: true,  defaultMonths: 12, improvement: true,  standards: false, appeal: true },
  { value: 'Final Written Warning', letter: true,  expiry: true,  defaultMonths: 12, improvement: true,  standards: true,  appeal: true },
  { value: 'Dismissal',             letter: true,  expiry: false, improvement: false, standards: false, appeal: false },
];

const typeConfig = (value) => ACTION_TYPES.find((t) => t.value === value) || {};

// Same keyword rules as the server, so older free-text records still get a "Generate letter" button.
const hasLetter = (value) => {
  const t = (value || '').toLowerCase();
  if (t.includes('verbal') || t.includes('informal')) return false;
  return ['dismiss', 'final', 'no action', 'warning'].some((k) => t.includes(k));
};

// One-off letters that are not tied to a saved action. Field names must match
// ADHOC_LETTERS in services/disciplinary_letters.py.
const LETTER_FORMS = {
  suspension: {
    title: 'Suspension Letter',
    button: 'Suspension letter',
    fields: [
      { name: 'allegations', label: 'Allegation(s)', required: true, multiline: true,
        helper: 'Printed in the letter under "pending an investigation into the following allegation(s)"' },
      { name: 'conversation_date', label: 'Date you spoke to the employee', type: 'date' },
      { name: 'support_contact', label: 'Support contact (name)', maxLength: 200 },
      { name: 'eap_number', label: 'Employee Assistance Programme number', maxLength: 100 },
    ],
  },
  hearing: {
    title: 'Disciplinary Hearing Invitation',
    button: 'Hearing invitation',
    fields: [
      { name: 'allegations', label: 'Allegation(s) to be considered', required: true, multiline: true,
        helper: 'Printed in the letter\'s Allegations box' },
      { name: 'hearing_date', label: 'Hearing date', type: 'date' },
      { name: 'hearing_time', label: 'Hearing time', maxLength: 50, helper: 'e.g. 10:00' },
      { name: 'hearing_location', label: 'Location', maxLength: 200 },
      { name: 'hearing_chair', label: 'Chair of the hearing', maxLength: 200 },
      { name: 'other_attendees', label: 'Also present', maxLength: 300 },
      { name: 'witness_deadline', label: 'Witness names needed by', type: 'date' },
      { name: 'evidence_deadline', label: 'Additional evidence needed by', type: 'date' },
    ],
  },
};

const EMPTY_ACTION = {
  type_of_action: '',
  date_action_taken: '',
  hearing_date: '',
  expiry_date: '',
  reason: '',
  improvement_required: '',
  standards_expected: '',
  support_measures: '',
  appeal_contact: '',
  details_of_appeal: '',
  generate_letter: true,
};

// 'YYYY-MM-DD' + n months, same day of month (clamped, e.g. 29 Feb -> 28 Feb)
const addMonths = (iso, months) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1 + months, d);
  if (dt.getDate() !== d) dt.setDate(0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const showDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB') : 'N/A');

const Disciplinary = ({ employee }) => {
  const [disciplinaryActions, setDisciplinaryActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newAction, setNewAction] = useState(EMPTY_ACTION);
  const [expiryEdited, setExpiryEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState(null);          // { severity, text }
  const [letterBusy, setLetterBusy] = useState({});    // action id -> true while generating
  const [letterDone, setLetterDone] = useState({});    // action id -> true once drafted
  const [letterKind, setLetterKind] = useState(null);  // 'suspension' | 'hearing' | null (dialog closed)
  const [letterFields, setLetterFields] = useState({});
  const [letterSaving, setLetterSaving] = useState(false);
  const [letterError, setLetterError] = useState('');

  useEffect(() => {
    if (employee && employee.secure_id) {
      fetchDisciplinaryActions(employee.secure_id);
    }
  }, [employee]);

  const fetchDisciplinaryActions = async (employeeId) => {
    try {
      setLoading(true);
      const response = await api.get(`/disciplinary/employee/${employeeId}`);
      setDisciplinaryActions(response.data);
    } catch (err) {
      console.error('Error fetching disciplinary actions:', err);
      setError('Failed to fetch disciplinary actions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewAction(EMPTY_ACTION);
    setExpiryEdited(false);
    setDialogError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const next = { ...newAction, [name]: value };
    const cfg = typeConfig(next.type_of_action);
    let edited = expiryEdited;

    if (name === 'expiry_date') edited = true;

    if (name === 'type_of_action') {
      // Clear anything the new type doesn't use, and start the expiry date afresh
      edited = false;
      next.expiry_date = '';
      if (!cfg.improvement) { next.improvement_required = ''; next.support_measures = ''; }
      if (!cfg.standards) next.standards_expected = '';
      if (!cfg.appeal) next.appeal_contact = '';
      next.generate_letter = !!cfg.letter;
    }

    // Pre-fill the expiry date until the user changes it themselves
    if ((name === 'type_of_action' || name === 'date_action_taken')
        && cfg.defaultMonths && next.date_action_taken && !edited) {
      next.expiry_date = addMonths(next.date_action_taken, cfg.defaultMonths);
    }

    setExpiryEdited(edited);
    setNewAction(next);
  };

  const handleSubmit = async () => {
    if (!newAction.type_of_action || !newAction.reason.trim() || !newAction.date_action_taken) {
      setDialogError('Please choose the type of action, the date and give a reason.');
      return;
    }
    try {
      setSaving(true);
      setDialogError('');
      const response = await api.post(`/disciplinary/employee/${employee.secure_id}`, newAction);
      const { letter, ...saved } = response.data;
      setDisciplinaryActions((prev) => [...prev, saved]);
      handleCloseDialog();

      if (letter && letter.generated) {
        setLetterDone((prev) => ({ ...prev, [saved.id]: true }));
        setNotice({ severity: 'success', text: `Saved. The ${letter.document_name || 'letter'} has been drafted - find it in your ${VAULT_NAME} to review.` });
      } else if (letter && !letter.generated) {
        setNotice({ severity: 'warning', text: 'Saved, but the letter could not be drafted. Use "Generate letter" on the row to try again.' });
      } else {
        setNotice({ severity: 'success', text: 'Disciplinary action saved.' });
      }
    } catch (err) {
      console.error('Error adding disciplinary action:', err);
      setDialogError(err.response?.data?.error || 'Failed to add disciplinary action');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateLetter = async (action) => {
    try {
      setLetterBusy((prev) => ({ ...prev, [action.id]: true }));
      const response = await api.post(`/disciplinary/employee/${employee.secure_id}/${action.id}/letter`);
      setLetterDone((prev) => ({ ...prev, [action.id]: true }));
      setNotice({ severity: 'success', text: `The ${response.data.document_name || 'letter'} has been drafted - find it in your ${VAULT_NAME} to review.` });
    } catch (err) {
      setNotice({ severity: 'error', text: err.response?.data?.error || 'The letter could not be generated.' });
    } finally {
      setLetterBusy((prev) => ({ ...prev, [action.id]: false }));
    }
  };

  const openLetterDialog = (kind) => {
    setLetterFields({});
    setLetterError('');
    setLetterKind(kind);
  };

  const closeLetterDialog = () => {
    setLetterKind(null);
    setLetterFields({});
    setLetterError('');
  };

  const handleLetterField = (e) => {
    const { name, value } = e.target;
    setLetterFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleLetterSubmit = async () => {
    const form = LETTER_FORMS[letterKind];
    if (!form) return;
    if (!(letterFields.allegations || '').trim()) {
      setLetterError('Please describe the allegation(s).');
      return;
    }
    try {
      setLetterSaving(true);
      setLetterError('');
      const response = await api.post(`/disciplinary/employee/${employee.secure_id}/letters/${letterKind}`, letterFields);
      closeLetterDialog();
      setNotice({ severity: 'success', text: `The ${response.data.document_name || form.title} has been drafted - find it in your ${VAULT_NAME} to review.` });
    } catch (err) {
      setLetterError(err.response?.data?.error || 'The letter could not be generated.');
    } finally {
      setLetterSaving(false);
    }
  };

  if (loading) return <Typography>Loading disciplinary actions...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  const cfg = typeConfig(newAction.type_of_action);

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Button variant="contained" color="primary" onClick={handleOpenDialog}>
          Add Disciplinary Action
        </Button>
        {Object.entries(LETTER_FORMS).map(([kind, form]) => (
          <Button key={kind} variant="outlined" onClick={() => openLetterDialog(kind)}>
            {form.button}
          </Button>
        ))}
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Appeal Details</TableCell>
              <TableCell>Letter</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {disciplinaryActions.map((action) => (
              <TableRow key={action.id}>
                <TableCell>{showDate(action.date_action_taken)}</TableCell>
                <TableCell>{action.type_of_action}</TableCell>
                <TableCell>{action.reason}</TableCell>
                <TableCell>{showDate(action.expiry_date)}</TableCell>
                <TableCell>{action.details_of_appeal || 'N/A'}</TableCell>
                <TableCell>
                  {hasLetter(action.type_of_action) ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!!letterBusy[action.id]}
                      onClick={() => handleGenerateLetter(action)}
                    >
                      {letterBusy[action.id] ? 'Working...' : letterDone[action.id] ? 'Regenerate letter' : 'Generate letter'}
                    </Button>
                  ) : 'N/A'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={saving ? undefined : handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Disciplinary Action</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mt: 1 }}>{dialogError}</Alert>}

          <TextField
            select
            required
            name="type_of_action"
            label="Type of Action"
            fullWidth
            margin="normal"
            value={newAction.type_of_action}
            onChange={handleInputChange}
          >
            {ACTION_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.value}</MenuItem>
            ))}
          </TextField>

          <TextField
            required
            name="date_action_taken"
            label="Date Action Taken"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newAction.date_action_taken}
            onChange={handleInputChange}
          />

          <TextField
            name="hearing_date"
            label="Hearing Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newAction.hearing_date}
            onChange={handleInputChange}
          />

          {cfg.expiry && (
            <TextField
              name="expiry_date"
              label="Expiry Date"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText={cfg.defaultMonths ? `Pre-filled as ${cfg.defaultMonths} months after the date above - change it if needed` : undefined}
              value={newAction.expiry_date}
              onChange={handleInputChange}
            />
          )}

          <TextField
            required
            name="reason"
            label="Reason / Findings"
            helperText="Printed in the letter's findings / reasoning box"
            fullWidth
            multiline
            minRows={3}
            margin="normal"
            value={newAction.reason}
            onChange={handleInputChange}
          />

          {cfg.improvement && (
            <>
              <TextField
                name="improvement_required"
                label="Improvement Required"
                helperText="Printed under 'You must immediately improve in the following areas'"
                fullWidth
                multiline
                minRows={3}
                margin="normal"
                value={newAction.improvement_required}
                onChange={handleInputChange}
              />
              <TextField
                name="support_measures"
                label="Support We Will Provide"
                fullWidth
                multiline
                minRows={2}
                margin="normal"
                value={newAction.support_measures}
                onChange={handleInputChange}
              />
            </>
          )}

          {cfg.standards && (
            <TextField
              name="standards_expected"
              label="Standards Expected / Moving Forward"
              helperText="Completes 'The standards expected are:' (final warning) or 'Moving Forward' (no action)"
              fullWidth
              multiline
              minRows={3}
              margin="normal"
              value={newAction.standards_expected}
              onChange={handleInputChange}
            />
          )}

          {cfg.appeal && (
            <TextField
              name="appeal_contact"
              label="Appeal To"
              helperText="Name and role of the person an appeal should be sent to"
              fullWidth
              margin="normal"
              inputProps={{ maxLength: 150 }}
              value={newAction.appeal_contact}
              onChange={handleInputChange}
            />
          )}

          <TextField
            name="details_of_appeal"
            label="Appeal Details"
            helperText="Record the outcome of any appeal here"
            fullWidth
            margin="normal"
            value={newAction.details_of_appeal}
            onChange={handleInputChange}
          />

          {cfg.letter && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newAction.generate_letter}
                    onChange={(e) => setNewAction((prev) => ({ ...prev, generate_letter: e.target.checked }))}
                  />
                }
                label={`Draft the letter when I save (goes to your ${VAULT_NAME} to review)`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} color="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!letterKind} onClose={letterSaving ? undefined : closeLetterDialog} fullWidth maxWidth="sm">
        <DialogTitle>{letterKind ? LETTER_FORMS[letterKind].title : ''}</DialogTitle>
        <DialogContent>
          {letterError && <Alert severity="error" sx={{ mt: 1 }}>{letterError}</Alert>}
          {letterKind && LETTER_FORMS[letterKind].fields.map((f) => (
            <TextField
              key={f.name}
              name={f.name}
              label={f.label}
              type={f.type || 'text'}
              required={!!f.required}
              multiline={!!f.multiline}
              minRows={f.multiline ? 4 : undefined}
              helperText={f.helper}
              fullWidth
              margin="normal"
              InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
              inputProps={f.maxLength ? { maxLength: f.maxLength } : undefined}
              value={letterFields[f.name] || ''}
              onChange={handleLetterField}
            />
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Anything you leave blank appears in the letter as a [blank] to complete by hand. The draft goes to your {VAULT_NAME}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLetterDialog} disabled={letterSaving}>Cancel</Button>
          <Button onClick={handleLetterSubmit} color="primary" disabled={letterSaving}>
            {letterSaving ? 'Drafting...' : 'Draft letter'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notice}
        autoHideDuration={8000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">{notice.text}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default Disciplinary;